import { useEffect, useMemo, useState } from "react";
import PostCallWebhookCard from "@/components/PostCallWebhookCard";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  Bot,
  Copy,
  Pencil,
  Phone,
  PhoneOutgoing,
  Play,
  Save,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { canEditOwn, useAuthStore } from "@/features/auth/store";
import {
  useAgent,
  useDeleteAgent,
  useDeployAgent,
  useDuplicateAgent,
  useUpdateAgent,
} from "@/features/agents/hooks";
import LiveVoicePicker from "@/features/agents/components/LiveVoicePicker";
import type { Agent, CreateAgentInput } from "@/features/agents/api";
import AgentChatTab from "@/features/agents/components/AgentChatTab";
import AgentAnalyticsTab from "@/features/agents/components/AgentAnalyticsTab";
import AgentFunctionsTab from "@/features/agents/components/AgentFunctionsTab";
import AgentTemplateTab from "@/features/agents/components/AgentTemplateTab";
import AgentVersionsTab from "@/features/agents/components/AgentVersionsTab";
import VoiceTestTab from "@/features/agents/components/VoiceTestTab";
import { useProviderOptions } from "@/features/assistants/hooks";
import { TOKEN_PRICE_HINT, tokenPriceText } from "@/features/assistants/api";
import { modelSpeed } from "@/features/assistants/components/settings/ProviderPicker";
import PlaceCallDialog from "@/features/telephony/components/PlaceCallDialog";
import { cn } from "@/lib/utils";
import { useGuardedAction } from "@/features/auth/access";

// LLM providers the backend accepts (agents.models.Agent.LLM_PROVIDERS).
const LLM_PROVIDERS: { value: string; label: string }[] = [
  { value: "openai", label: "OpenAI" },
  { value: "anthropic", label: "Anthropic" },
  { value: "groq", label: "Groq" },
  { value: "custom", label: "Custom" },
];

// Models come from the platform catalog (Admin → Provider options), labelled
// by how fast they start replying on a call. "Custom…" still takes any model
// ID the provider supports (and is the only option for the "custom" provider).
function useLlmModels() {
  const { options } = useProviderOptions("model");
  return (provider: string) =>
    options
      .filter((o) => o.provider === provider && o.api_id)
      .map((o) => {
        const price = tokenPriceText(o, true);
        return { value: o.api_id ?? "", label: `${o.name} — ${modelSpeed(o.latency_ms)}${price ? ` · ${price}` : ""}`, priced: !!price };
      });
}

const CUSTOM_MODEL = "__custom__";

const STATUS_STYLES: Record<string, string> = {
  active:
    "text-emerald-600 bg-emerald-50 dark:bg-emerald-950/40 dark:text-emerald-400",
  draft:
    "text-slate-600 bg-slate-100 dark:bg-slate-800 dark:text-slate-300",
  paused:
    "text-amber-600 bg-amber-50 dark:bg-amber-950/40 dark:text-amber-400",
  inactive:
    "text-rose-600 bg-rose-50 dark:bg-rose-950/40 dark:text-rose-400",
};

function StatusBadge({ status }: { status: string }) {
  const cls = STATUS_STYLES[status] ?? "text-muted-foreground bg-muted";
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium capitalize",
        cls,
      )}
    >
      {status}
    </span>
  );
}

export default function AgentDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const role = useAuthStore((s) => s.user?.role ?? "member");
  // Customers own everything they can see here, so rank does not apply.
  const canEdit = canEditOwn(role);

  const q = useAgent(id);
  const agent = q.data;

  const deploy = useDeployAgent();
  const duplicate = useDuplicateAgent();
  const remove = useDeleteAgent();

  const [deleteOpen, setDeleteOpen] = useState(false);
  const [placeCallOpen, setPlaceCallOpen] = useState(false);
  const guard = useGuardedAction();

  async function onDeploy() {
    if (!agent) return;
    try {
      await deploy.mutateAsync(agent.id);
      toast.success("Agent deployed");
    } catch {
      toast.error("Failed to deploy agent");
    }
  }

  async function onDuplicate() {
    if (!agent) return;
    try {
      await duplicate.mutateAsync(agent.id);
      toast.success("Agent duplicated");
      navigate("/agents");
    } catch {
      toast.error("Failed to duplicate agent");
    }
  }

  async function onConfirmDelete() {
    if (!agent) return;
    try {
      await remove.mutateAsync(agent.id);
      toast.success("Agent deleted");
      navigate("/agents");
    } catch {
      toast.error("Failed to delete agent");
    }
  }

  if (q.isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-1/3" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (q.isError || !agent) {
    return (
      <div className="space-y-4">
        <Button variant="ghost" size="sm" asChild>
          <Link to="/agents">
            <ArrowLeft className="size-4" />
            Back to Agents
          </Link>
        </Button>
        <Card>
          <CardContent className="py-10 text-center text-sm text-destructive">
            Failed to load agent.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start gap-3">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate("/agents")}
          aria-label="Back"
          className="mt-1"
        >
          <ArrowLeft className="size-5" />
        </Button>
        <div className="flex min-w-0 flex-1 items-center gap-3">
          <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-indigo-100 text-indigo-600 dark:bg-indigo-950/50 dark:text-indigo-400">
            <Bot className="size-5" />
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="truncate text-2xl font-semibold tracking-tight">
                {agent.name}
              </h1>
              <StatusBadge status={agent.status} />
            </div>
            {agent.description && (
              <p className="mt-0.5 truncate text-sm text-muted-foreground">
                {agent.description}
              </p>
            )}
          </div>
        </div>
        {canEdit && (
          <div className="flex shrink-0 flex-wrap items-center gap-1.5">
            <Button size="sm" onClick={() => guard(() => setPlaceCallOpen(true))}>
              <PhoneOutgoing className="size-4" />
              Place Call
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDeploy}
              disabled={deploy.isPending}
            >
              <Play className="size-4" />
              Deploy
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={onDuplicate}
              disabled={duplicate.isPending}
            >
              <Copy className="size-4" />
              Duplicate
            </Button>
            <Button
              variant="outline"
              size="sm"
              className="text-destructive hover:text-destructive"
              onClick={() => setDeleteOpen(true)}
            >
              <Trash2 className="size-4" />
              Delete
            </Button>
          </div>
        )}
      </div>

      <Tabs defaultValue="overview" className="w-full">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="template">Template</TabsTrigger>
          <TabsTrigger value="config">Configuration</TabsTrigger>
          <TabsTrigger value="prompts">Single Prompt</TabsTrigger>
          <TabsTrigger value="functions">Functions</TabsTrigger>
          <TabsTrigger value="chat">Test Chat</TabsTrigger>
          <TabsTrigger value="voice">
            <Phone className="size-3.5" />
            Voice Test
          </TabsTrigger>
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="webhook">Webhook</TabsTrigger>
          <TabsTrigger value="versions">Versions</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">
          <OverviewTab agent={agent} />
        </TabsContent>

        <TabsContent value="template">
          <AgentTemplateTab agent={agent} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="config">
          <ConfigurationTab
            agent={agent}
            canEdit={canEdit}
          />
        </TabsContent>

        <TabsContent value="prompts">
          <PromptsTab agent={agent} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="functions">
          <AgentFunctionsTab agentId={agent.id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="chat">
          <AgentChatTab
            agentId={agent.id}
            voiceProvider={agent.voice_provider}
            voiceId={agent.voice_id}
            ttsModel={agent.voice_settings?.tts_model as string | undefined}
          />
        </TabsContent>

        <TabsContent value="voice">
          <VoiceTestTab agentId={agent.id} agentName={agent.name} />
        </TabsContent>

        <TabsContent value="analytics">
          <AgentAnalyticsTab agentId={agent.id} />
        </TabsContent>

        <TabsContent value="webhook">
          <PostCallWebhookCard owner="agents" id={agent.id} canEdit={canEdit} />
        </TabsContent>

        <TabsContent value="versions">
          <AgentVersionsTab agent={agent} canEdit={canEdit} />
        </TabsContent>
      </Tabs>

      <AlertDialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <AlertDialogContent className="max-w-md">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete agent</AlertDialogTitle>
            <AlertDialogDescription>
              Delete{" "}
              <span className="font-medium text-foreground">{agent.name}</span>?
              This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={remove.isPending}>
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              disabled={remove.isPending}
              onClick={(e) => {
                e.preventDefault();
                onConfirmDelete();
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {remove.isPending ? "Deleting…" : "Delete"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {placeCallOpen && (
        <PlaceCallDialog
          open={placeCallOpen}
          onOpenChange={setPlaceCallOpen}
          lockedAgentId={agent.id}
        />
      )}
    </div>
  );
}

function OverviewTab({ agent }: { agent: Agent }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Overview</CardTitle>
        <CardDescription>
          Read-only summary. Edit details in the other tabs.
        </CardDescription>
      </CardHeader>
      <CardContent>
        <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
          <Row label="Status" value={agent.status} />
          <Row label="Type" value={agent.agent_type || "—"} />
          <Row label="Language" value={agent.language || "—"} />
          <Row label="Telnyx pipeline" value={agent.pipeline_version || "—"} />
          <Row label="Voice provider" value={agent.voice_provider || "—"} />
          <Row
            label="Voice"
            value={(agent.voice_settings?.voice_name as string) || agent.voice_id || "—"}
          />
          <Row label="LLM provider" value={agent.llm_provider || "—"} />
          <Row label="LLM model" value={agent.llm_model || "—"} />
          <Row
            label="Total calls"
            value={(agent.total_calls ?? 0).toLocaleString()}
          />
          <Row
            label="Total minutes"
            value={(agent.total_minutes ?? 0).toLocaleString()}
          />
        </dl>
      </CardContent>
    </Card>
  );
}

function Row({ label, value }: { label: string; value: string | number }) {
  return (
    <div className="flex flex-col gap-0.5">
      <dt className="text-xs uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="break-words text-sm">{String(value) || "—"}</dd>
    </div>
  );
}

const STT_PROVIDER_LABELS: Record<string, string> = {
  deepgram: "Deepgram",
  flux: "Deepgram Flux",
  assemblyai: "AssemblyAI",
  gladia: "Gladia",
};
function sttLabel(p?: string) {
  return STT_PROVIDER_LABELS[p || "deepgram"] || p || "Deepgram";
}

const PACE_LABELS: Record<string, string> = {
  snappy: "Snappy",
  balanced: "Balanced",
  relaxed: "Relaxed",
};
function paceLabel(p?: string) {
  return PACE_LABELS[p || "balanced"] || p || "Balanced";
}

function ConfigurationTab({
  agent,
  canEdit,
}: {
  agent: Agent;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState<Partial<CreateAgentInput>>({});
  const update = useUpdateAgent(agent.id);
  const llmModels = useLlmModels();

  useEffect(() => {
    if (editing) {
      setForm({
        agent_type: agent.agent_type,
        pipeline_version: agent.pipeline_version,
        language: agent.language,
        multilingual: agent.multilingual ?? false,
        record_calls: agent.record_calls ?? true,
        stt_provider: agent.stt_provider ?? "deepgram",
        response_pace: agent.response_pace ?? "balanced",
        speculative_replies: agent.speculative_replies ?? false,
        voice_provider: agent.voice_provider,
        voice_id: agent.voice_id,
        voice_settings: agent.voice_settings ?? {},
        llm_provider: agent.llm_provider,
        llm_model: agent.llm_model,
        voicemail_detection_enabled: agent.voicemail_detection_enabled ?? true,
        voicemail_message: agent.voicemail_message ?? "",
        followup_sms_enabled: agent.followup_sms_enabled ?? false,
        followup_email_enabled: agent.followup_email_enabled ?? false,
        followup_template: agent.followup_template ?? "",
        followup_booking_link: agent.followup_booking_link ?? "",
        hot_lead_routing_enabled: agent.hot_lead_routing_enabled ?? false,
        hot_lead_inapp_enabled: agent.hot_lead_inapp_enabled ?? true,
        hot_lead_email_enabled: agent.hot_lead_email_enabled ?? false,
        hot_lead_email_recipients: agent.hot_lead_email_recipients ?? "",
        hot_lead_sms_enabled: agent.hot_lead_sms_enabled ?? false,
        hot_lead_sms_recipients: agent.hot_lead_sms_recipients ?? "",
      });
    }
  }, [editing, agent]);

  async function save() {
    try {
      await update.mutateAsync(form);
      toast.success("Configuration updated");
      setEditing(false);
    } catch {
      toast.error("Failed to update configuration");
    }
  }

  if (!editing) {
    return (
      <Card>
        <CardHeader className="flex-row items-start justify-between space-y-0">
          <div>
            <CardTitle className="text-base">Configuration</CardTitle>
            <CardDescription>
              Pipeline, voice and LLM settings.
            </CardDescription>
          </div>
          {canEdit && (
            <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
              <Pencil className="size-4" />
              Edit
            </Button>
          )}
        </CardHeader>
        <CardContent>
          <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
            <Row label="Type" value={agent.agent_type || "—"} />
            <Row label="Language" value={agent.language || "—"} />
            <Row
              label="Call recording"
              value={agent.record_calls ?? true ? "On" : "Off"}
            />
            <Row label="Voice provider" value={agent.voice_provider || "—"} />
            <Row
              label="Voice"
              value={(agent.voice_settings?.voice_name as string) || agent.voice_id || "—"}
            />
            <Row label="LLM provider" value={agent.llm_provider || "—"} />
            <Row label="LLM model" value={agent.llm_model || "—"} />
            <Row
              label="Voicemail detection"
              value={agent.voicemail_detection_enabled ? "On" : "Off"}
            />
            <Row
              label="Voicemail message"
              value={agent.voicemail_message || "— (hang up on voicemail)"}
            />
            <Row
              label="Follow-up SMS"
              value={agent.followup_sms_enabled ? "On" : "Off"}
            />
            <Row
              label="Follow-up email"
              value={agent.followup_email_enabled ? "On" : "Off"}
            />
            <Row
              label="Follow-up booking link"
              value={agent.followup_booking_link || "—"}
            />
            <Row
              label="Hot-lead routing"
              value={agent.hot_lead_routing_enabled ? "On" : "Off"}
            />
            <Row
              label="Hot-lead channels"
              value={
                agent.hot_lead_routing_enabled
                  ? [
                      agent.hot_lead_inapp_enabled && "In-app",
                      agent.hot_lead_email_enabled && "Email",
                      agent.hot_lead_sms_enabled && "SMS",
                    ]
                      .filter(Boolean)
                      .join(", ") || "— (none selected)"
                  : "—"
              }
            />
          </dl>
          <div className="mt-6 border-t pt-4">
            <h4 className="text-sm font-medium">Telnyx numbers only</h4>
            <p className="mb-3 text-xs text-muted-foreground">
              These settings apply to calls on Telnyx numbers. Twilio numbers
              use the Twilio voice pipeline (Deepgram speech-to-text) and
              ignore them.
            </p>
            <dl className="grid grid-cols-1 gap-x-6 gap-y-3 text-sm sm:grid-cols-2">
              <Row label="Pipeline" value={agent.pipeline_version || "—"} />
              <Row
                label="Speech-to-text"
                value={sttLabel(agent.stt_provider)}
              />
              <Row
                label="Response pace"
                value={paceLabel(agent.response_pace)}
              />
              {agent.stt_provider === "flux" && (
                <Row
                  label="Speculative replies"
                  value={agent.speculative_replies ? "On" : "Off"}
                />
              )}
              <Row
                label="Multilingual mode"
                value={agent.multilingual ? "On" : "Off"}
              />
            </dl>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Edit configuration</CardTitle>
          <CardDescription>
            Changes save immediately when you click Save.
          </CardDescription>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setEditing(false)}
          disabled={update.isPending}
        >
          <X className="size-4" />
          Cancel
        </Button>
      </CardHeader>
      <CardContent className="grid gap-4 sm:grid-cols-2">
        <div className="space-y-1.5">
          <Label>Type</Label>
          <Select
            value={form.agent_type ?? ""}
            onValueChange={(v) => setForm({ ...form, agent_type: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="single_prompt">Single Prompt</SelectItem>
              <SelectItem value="flow_based">Flow Based</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Language</Label>
          <Select
            value={form.language ?? "en-US"}
            onValueChange={(v) => setForm({ ...form, language: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="en-US">English</SelectItem>
              <SelectItem value="es-ES">Spanish</SelectItem>
              <SelectItem value="fr-FR">French</SelectItem>
              <SelectItem value="hi-IN">Hindi</SelectItem>
              <SelectItem value="ar-SA">Arabic</SelectItem>
              <SelectItem value="de-DE">German</SelectItem>
              <SelectItem value="pt-BR">Portuguese (Brazil)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Primary language used by speech recognition and the LLM.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Call recording</Label>
          <Select
            value={(form.record_calls ?? true) ? "on" : "off"}
            onValueChange={(v) =>
              setForm({ ...form, record_calls: v === "on" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="on">On (record calls)</SelectItem>
              <SelectItem value="off">Off (no recording)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            On = the call audio is recorded and playable from the call log.
            Off = no recording is captured for this agent.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Voice provider</Label>
          <Select
            value={form.voice_provider ?? ""}
            onValueChange={(v) =>
              setForm({
                ...form,
                voice_provider: v,
                voice_id: "",
                voice_settings: { ...(form.voice_settings ?? {}), voice_name: "" },
              })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="elevenlabs">ElevenLabs</SelectItem>
              <SelectItem value="cartesia">Cartesia</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Voice</Label>
          <LiveVoicePicker
            provider={form.voice_provider ?? ""}
            value={form.voice_id ?? ""}
            valueName={(form.voice_settings?.voice_name as string) ?? ""}
            onSelect={(v) =>
              setForm((p) => ({
                ...p,
                voice_id: v.voice_id,
                voice_settings: { ...(p.voice_settings ?? {}), voice_name: v.name },
              }))
            }
          />
        </div>
        <div className="space-y-1.5">
          <Label>LLM provider</Label>
          <Select
            value={form.llm_provider ?? "openai"}
            onValueChange={(v) => {
              // Switching provider picks that provider's default model so the
              // model field is never left pointing at another provider's model.
              const opts = llmModels(v);
              setForm({
                ...form,
                llm_provider: v,
                llm_model: opts.length ? opts[0].value : "",
              });
            }}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {LLM_PROVIDERS.map((p) => (
                <SelectItem key={p.value} value={p.value}>
                  {p.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>LLM model</Label>
          {(() => {
            const opts = llmModels(form.llm_provider ?? "openai");
            const isCustom =
              opts.length === 0 ||
              !opts.some((m) => m.value === form.llm_model);
            return (
              <>
                {opts.length > 0 && (
                  <Select
                    value={isCustom ? CUSTOM_MODEL : (form.llm_model ?? "")}
                    onValueChange={(v) =>
                      setForm({
                        ...form,
                        llm_model: v === CUSTOM_MODEL ? "" : v,
                      })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {opts.map((m) => (
                        <SelectItem key={m.value} value={m.value}>
                          {m.label}
                        </SelectItem>
                      ))}
                      <SelectItem value={CUSTOM_MODEL}>Custom…</SelectItem>
                    </SelectContent>
                  </Select>
                )}
                {isCustom && (
                  <Input
                    value={form.llm_model ?? ""}
                    onChange={(e) =>
                      setForm({ ...form, llm_model: e.target.value })
                    }
                    placeholder="Model ID from your provider (e.g. gpt-5.6-luna)"
                  />
                )}
                <p className="text-xs text-muted-foreground">
                  On calls, pick a Fastest or Fast model. Slower models leave a
                  noticeable pause before every reply.
                  {opts.some((m) => m.priced) && ` ${TOKEN_PRICE_HINT}`}
                </p>
              </>
            );
          })()}
        </div>
        <div className="space-y-1.5">
          <Label>Voicemail detection (outbound)</Label>
          <Select
            value={form.voicemail_detection_enabled ? "on" : "off"}
            onValueChange={(v) =>
              setForm({ ...form, voicemail_detection_enabled: v === "on" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="on">On — detect human vs voicemail</SelectItem>
              <SelectItem value="off">Off — connect immediately</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Voicemail message</Label>
          <Textarea
            value={form.voicemail_message ?? ""}
            onChange={(e) =>
              setForm({ ...form, voicemail_message: e.target.value })
            }
            placeholder="Left when an outbound call reaches voicemail. Empty = hang up instead."
          />
        </div>

        <div className="sm:col-span-2 border-t pt-4">
          <p className="text-sm font-medium">Post-call follow-up</p>
          <p className="text-xs text-muted-foreground">
            After the call ends, automatically send the caller a recap (summary
            + next steps + optional link). SMS uses your workspace's
            SMS-capable Telnyx number; email is sent only when an address was
            captured during the call.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Follow-up SMS</Label>
          <Select
            value={form.followup_sms_enabled ? "on" : "off"}
            onValueChange={(v) =>
              setForm({ ...form, followup_sms_enabled: v === "on" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off</SelectItem>
              <SelectItem value="on">On — text the caller a recap</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Follow-up email</Label>
          <Select
            value={form.followup_email_enabled ? "on" : "off"}
            onValueChange={(v) =>
              setForm({ ...form, followup_email_enabled: v === "on" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off</SelectItem>
              <SelectItem value="on">On — email the caller a recap</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Follow-up booking link (optional)</Label>
          <Input
            value={form.followup_booking_link ?? ""}
            onChange={(e) =>
              setForm({ ...form, followup_booking_link: e.target.value })
            }
            placeholder="https://… booking, payment or menu link appended to the recap"
          />
        </div>
        <div className="space-y-1.5 sm:col-span-2">
          <Label>Follow-up message (optional)</Label>
          <Textarea
            value={form.followup_template ?? ""}
            onChange={(e) =>
              setForm({ ...form, followup_template: e.target.value })
            }
            placeholder={
              "Custom recap text. Leave blank to auto-build from the call summary.\n" +
              "Placeholders: {summary}, {next_steps}, {agent_name}, {booking_link}"
            }
          />
        </div>

        <div className="sm:col-span-2 border-t pt-4">
          <p className="text-sm font-medium">Hot-lead routing</p>
          <p className="text-xs text-muted-foreground">
            When a call handled by this agent scores as a hot lead, alert the
            team so they can act while it's warm. Pick any channels below.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Hot-lead routing</Label>
          <Select
            value={form.hot_lead_routing_enabled ? "on" : "off"}
            onValueChange={(v) =>
              setForm({ ...form, hot_lead_routing_enabled: v === "on" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off</SelectItem>
              <SelectItem value="on">On — alert on hot leads</SelectItem>
            </SelectContent>
          </Select>
        </div>
        {form.hot_lead_routing_enabled && (
          <>
            <div className="space-y-1.5">
              <Label>In-app notification</Label>
              <Select
                value={form.hot_lead_inapp_enabled ? "on" : "off"}
                onValueChange={(v) =>
                  setForm({ ...form, hot_lead_inapp_enabled: v === "on" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="on">
                    On — notify owners/admins/managers
                  </SelectItem>
                  <SelectItem value="off">Off</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Email alert</Label>
              <Select
                value={form.hot_lead_email_enabled ? "on" : "off"}
                onValueChange={(v) =>
                  setForm({ ...form, hot_lead_email_enabled: v === "on" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="on">On</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.hot_lead_email_enabled && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>Email recipients</Label>
                <Input
                  value={form.hot_lead_email_recipients ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      hot_lead_email_recipients: e.target.value,
                    })
                  }
                  placeholder="comma-separated emails — blank = workspace owners/admins/managers"
                />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>SMS alert</Label>
              <Select
                value={form.hot_lead_sms_enabled ? "on" : "off"}
                onValueChange={(v) =>
                  setForm({ ...form, hot_lead_sms_enabled: v === "on" })
                }
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="off">Off</SelectItem>
                  <SelectItem value="on">On</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {form.hot_lead_sms_enabled && (
              <div className="space-y-1.5 sm:col-span-2">
                <Label>SMS recipient numbers</Label>
                <Input
                  value={form.hot_lead_sms_recipients ?? ""}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      hot_lead_sms_recipients: e.target.value,
                    })
                  }
                  placeholder="comma-separated E.164 numbers, e.g. +14155551234, +14155556789"
                />
              </div>
            )}
          </>
        )}

        <div className="sm:col-span-2 mt-2 border-t pt-4">
          <h4 className="text-sm font-medium">Telnyx numbers only</h4>
          <p className="text-xs text-muted-foreground">
            These settings apply to calls on Telnyx numbers. Twilio numbers use
            the Twilio voice pipeline (Deepgram speech-to-text) and ignore
            them.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Pipeline</Label>
          <Select
            value={form.pipeline_version ?? ""}
            onValueChange={(v) => setForm({ ...form, pipeline_version: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="v1" disabled>
                v1 — Legacy (retired)
              </SelectItem>
              <SelectItem value="v2" disabled>
                v2 — Streaming (legacy)
              </SelectItem>
              <SelectItem value="v3">v3 — Low-latency (recommended)</SelectItem>
              <SelectItem value="v4">v4 — Flux (eager, lowest-latency)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Speech-to-text provider</Label>
          <Select
            value={form.stt_provider ?? "deepgram"}
            onValueChange={(v) => setForm({ ...form, stt_provider: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="deepgram">Deepgram (default)</SelectItem>
              <SelectItem value="flux">
                Deepgram Flux (fastest turn-taking)
              </SelectItem>
              <SelectItem value="assemblyai">AssemblyAI</SelectItem>
              <SelectItem value="gladia">
                Gladia (best for language-switching)
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Engine that transcribes the caller in real time. Add the provider's
            API key under Settings → API Providers. Flux runs on the same
            Deepgram key and ends turns itself rather than waiting out a
            silence, so replies start sooner.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label>Response pace</Label>
          <Select
            value={form.response_pace ?? "balanced"}
            onValueChange={(v) => setForm({ ...form, response_pace: v })}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="snappy">
                Snappy — replies fast (may interrupt)
              </SelectItem>
              <SelectItem value="balanced">Balanced — default</SelectItem>
              <SelectItem value="relaxed">
                Relaxed — waits longer before replying
              </SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            How long the agent waits after the caller stops talking. Raise it if
            the agent keeps cutting callers off.
          </p>
        </div>
        {form.stt_provider === "flux" && (
          <div className="space-y-1.5">
            <Label>Speculative replies</Label>
            <Select
              value={form.speculative_replies ? "on" : "off"}
              onValueChange={(v) =>
                setForm({ ...form, speculative_replies: v === "on" })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="off">Off — default</SelectItem>
                <SelectItem value="on">On — start replying sooner</SelectItem>
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">
              Flux predicts when the caller is about to finish. With this on the
              reply is written during that prediction, so it can be spoken the
              moment they stop. A wrong prediction costs an extra LLM call — at
              most two per turn — and anything needing a tool always waits for
              the caller to finish.
            </p>
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Multilingual mode</Label>
          <Select
            value={form.multilingual ? "on" : "off"}
            onValueChange={(v) =>
              setForm({ ...form, multilingual: v === "on" })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="off">Off (single language)</SelectItem>
              <SelectItem value="on">On (auto-detect)</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            On = auto-detect caller's language (works best with ElevenLabs).
            Off = stay in the primary language — recommended for accuracy.
          </p>
        </div>

        <div className="sm:col-span-2 flex justify-end">
          <Button onClick={save} disabled={update.isPending}>
            <Save className="size-4" />
            {update.isPending ? "Saving…" : "Save"}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function PromptsTab({
  agent,
  canEdit,
}: {
  agent: Agent;
  canEdit: boolean;
}) {
  const [editing, setEditing] = useState(false);
  const [systemPrompt, setSystemPrompt] = useState(agent.system_prompt ?? "");
  const [initialMessage, setInitialMessage] = useState(
    agent.initial_message ?? "",
  );
  const [leadCriteria, setLeadCriteria] = useState(agent.lead_criteria ?? "");
  const update = useUpdateAgent(agent.id);

  useEffect(() => {
    if (editing) {
      setSystemPrompt(agent.system_prompt ?? "");
      setInitialMessage(agent.initial_message ?? "");
      setLeadCriteria(agent.lead_criteria ?? "");
    }
  }, [editing, agent]);

  async function save() {
    try {
      await update.mutateAsync({
        system_prompt: systemPrompt,
        initial_message: initialMessage,
        lead_criteria: leadCriteria,
      });
      toast.success("Prompts updated");
      setEditing(false);
    } catch {
      toast.error("Failed to update prompts");
    }
  }

  return (
    <Card>
      <CardHeader className="flex-row items-start justify-between space-y-0">
        <div>
          <CardTitle className="text-base">Prompts</CardTitle>
          <CardDescription>
            System prompt and the initial spoken message.
          </CardDescription>
        </div>
        {canEdit && !editing && (
          <Button
            variant="outline"
            size="sm"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-4" />
            Edit
          </Button>
        )}
        {editing && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setEditing(false)}
            disabled={update.isPending}
          >
            <X className="size-4" />
            Cancel
          </Button>
        )}
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <Label>System Prompt</Label>
          <Textarea
            rows={10}
            value={editing ? systemPrompt : agent.system_prompt ?? ""}
            onChange={(e) => setSystemPrompt(e.target.value)}
            disabled={!editing}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Initial Message</Label>
          <Textarea
            rows={3}
            value={editing ? initialMessage : agent.initial_message ?? ""}
            onChange={(e) => setInitialMessage(e.target.value)}
            disabled={!editing}
            placeholder="Message spoken when the call starts…"
          />
        </div>
        <div className="space-y-1.5">
          <Label>Lead Criteria</Label>
          <Textarea
            rows={4}
            value={editing ? leadCriteria : agent.lead_criteria ?? ""}
            onChange={(e) => setLeadCriteria(e.target.value)}
            disabled={!editing}
            placeholder={
              "What makes a hot lead for this agent? Used by AI lead scoring.\n" +
              "Example: a hot lead asks to book a table, gives a date/time or " +
              "party size, or asks about the menu. Leave blank to score from " +
              "this agent's purpose automatically."
            }
          />
          <p className="text-xs text-muted-foreground">
            Guides the 0–100 lead score for calls handled by this agent. Blank =
            scored from the agent's purpose above, automatically.
          </p>
        </div>
        {editing && (
          <div className="flex justify-end">
            <Button onClick={save} disabled={update.isPending}>
              <Save className="size-4" />
              {update.isPending ? "Saving…" : "Save"}
            </Button>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

